import {
  AutoComplete,
  FormContainer,
  ISelectType,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { Grid } from "@mui/material";
import { HTMLAttributes, memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IProfileTicketDefaultDocCategory } from "../../../../../../models/profile-ticket-default-doc-category";
import { listTicketDefaultDocCategories } from "../../../../../../services/ticketDefaultDocCategory.service";
import {
  createProfileTicketDefaultDocCategories,
  updateProfileTicketDefaultDocCategories,
} from "../../../../../../services/profileTicketDefaultDocCategories.service";
import {
  profileTicketDefaultDocCategorySchema,
  TProfileTicketDefaultDocCategoryForm,
} from "./profileTicketDefaultDocCategorySchema";

const translationPath = "page.register.roles.";

const listTicketDefaultDocCategoryOptions = async (params?: any) => {
  const response = await listTicketDefaultDocCategories(params);

  return {
    ...response,
    data: (response.data ?? []).map((item) => ({
      ...item,
      code: item.code != null ? String(item.code) : undefined,
    })),
  };
};

interface IProfileTicketDefaultDocCategoryProps {
  id?: string;
  roleId?: string;
  rows: IProfileTicketDefaultDocCategory[];
  onBack: () => void;
}

export const ProfileTicketDefaultDocCategory = memo(
  ({ id, roleId, rows, onBack }: IProfileTicketDefaultDocCategoryProps) => {
    const { t } = useTranslation();

    const { sendRequest: create, loading: cLoading } = useFetch(
      createProfileTicketDefaultDocCategories
    );

    const { sendRequest: update, loading: uLoading } = useFetch(
      updateProfileTicketDefaultDocCategories
    );

    const row = useMemo(
      () => rows.find((currentRow) => currentRow.id === id) ?? null,
      [rows, id]
    );

    const getCategoryOptionLabel = useCallback((option: ISelectType) => {
      const name = option?.name ?? "";
      const code = option?.code;

      if (code === undefined || code === null || code === "") return name;

      return `#${code} - ${name}`;
    }, []);

    const renderCategoryOption = useCallback(
      (props: HTMLAttributes<HTMLLIElement>, option: ISelectType) => (
        <li {...props} key={option.id}>
          {getCategoryOptionLabel(option)}
        </li>
      ),
      [getCategoryOptionLabel]
    );

    const getHandler = useCallback(
      async (): Promise<Treturn<TProfileTicketDefaultDocCategoryForm>> => {
        const formData: TProfileTicketDefaultDocCategoryForm = {
          ticketDefaultDocCategory: row
            ? {
                id: row.ticketDefaultDocCategory.id,
                name: row.ticketDefaultDocCategory.name,
                code: row.ticketDefaultDocCategory.code,
              }
            : null,
        };

        return {
          data: formData,
          success: true,
        };
      },
      [row]
    );

    const updateCategoryIds = useCallback(
      (categoryId: string) => {
        return Array.from(
          new Set(
            rows.map((currentRow) =>
              currentRow.id === row?.id
                ? categoryId
                : currentRow.ticketDefaultDocCategoryId
            )
          )
        );
      },
      [row?.id, rows]
    );

    const saveHandler = (v: TProfileTicketDefaultDocCategoryForm) =>
      create({
        roleId: roleId ?? "",
        ticketDefaultDocCategoryIds: v.ticketDefaultDocCategory
          ? [v.ticketDefaultDocCategory.id]
          : [],
      });

    const updateHandler = (v: TProfileTicketDefaultDocCategoryForm) =>
      update({
        id: roleId ?? "",
        item: {
          roleId: roleId ?? "",
          ticketDefaultDocCategoryIds: v.ticketDefaultDocCategory
            ? updateCategoryIds(v.ticketDefaultDocCategory.id)
            : rows.map((currentRow) => currentRow.ticketDefaultDocCategoryId),
        },
      });

    const loading = cLoading || uLoading;

    return (
      <FormContainer<TProfileTicketDefaultDocCategoryForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "ticket_default_doc_category")}
        schema={profileTicketDefaultDocCategorySchema}
        isModalForm={true}
        subtitle={
          id
            ? t(translationPath + "edit_link")
            : t(translationPath + "add_ticket_default_doc_category")
        }
        backButtonOnClick={onBack}
        redirectOnSave={false}
        onSuccess={onBack}
        propId={id}
        formActionsOnBottom={false}
        fields={() => (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <AutoComplete
                request={listTicketDefaultDocCategoryOptions}
                name="ticketDefaultDocCategory"
                size="small"
                getOptionLabel={getCategoryOptionLabel}
                renderOption={renderCategoryOption}
                label={t(translationPath + "ticket_default_doc_category")}
              />
            </Grid>
          </Grid>
        )}
      />
    );
  }
);
